import React, { useState, useEffect } from 'react';
import './App.css';
import { questions } from './data/questions';
import { determineProfile, profiles } from './utils/profileMapping';
import LandingPage from './components/LandingPage';
import LeadCapture from './components/LeadCapture';
import QuestionPhase from './components/QuestionPhase';
import ResultsPage from './components/ResultsPage';

function App() {
  const [currentStep, setCurrentStep] = useState('landing'); // landing, leadCapture, phase1, phase2, phase3, results
  const [leadData, setLeadData] = useState({});
  const [answers, setAnswers] = useState({});
  const [userProfile, setUserProfile] = useState(null);

  // Save to localStorage on every change
  useEffect(() => {
    localStorage.setItem('planningDnaProgress', JSON.stringify({
      currentStep,
      leadData,
      answers
    }));
  }, [currentStep, leadData, answers]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('planningDnaProgress');
    if (saved) {
      const data = JSON.parse(saved);
      if (data.currentStep && data.currentStep !== 'results') {
        setCurrentStep(data.currentStep);
        setLeadData(data.leadData || {});
        setAnswers(data.answers || {});
      }
    }
  }, []);

  const handleStartQuiz = () => {
    setCurrentStep('leadCapture');
  };

  const handleLeadCaptureComplete = (data) => {
    setLeadData(data);
    setCurrentStep('phase1');
  };

  const handlePhaseComplete = (phaseAnswers) => {
    const updatedAnswers = { ...answers, ...phaseAnswers };
    setAnswers(updatedAnswers);

    // Move to next phase
    if (currentStep === 'phase1') {
      setCurrentStep('phase2');
    } else if (currentStep === 'phase2') {
      setCurrentStep('phase3');
    } else if (currentStep === 'phase3') {
      // Calculate profile
      const profile = determineProfile(updatedAnswers);
      setUserProfile(profile);
      
      // Save lead data and answers to Google Sheets
      saveToGoogleSheets({
        ...leadData,
        answers: updatedAnswers,
        profile,
        completedAt: new Date().toISOString()
      });
      
      setCurrentStep('results');
    }
  };

  const saveToGoogleSheets = async (data) => {
    const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbx4jMJZFqqt1rUf8ZBmaxMnbbV8lzgaJE4pbNcRNjkozmiFn4e6syQURDfEWEint8DE2w/exec';
    
    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors', // Required for Google Apps Script
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      
      console.log('Data sent to Google Sheets successfully');
    } catch (error) {
      console.error('Error saving to Google Sheets:', error);
      // Don't show error to user - just log it
    }
  };

  const handleRestart = () => {
    localStorage.removeItem('planningDnaProgress');
    setCurrentStep('landing');
    setLeadData({});
    setAnswers({});
    setUserProfile(null);
  };

  return (
    <div className="App">
      {currentStep === 'landing' && (
        <LandingPage onStart={handleStartQuiz} />
      )}
      
      {currentStep === 'leadCapture' && (
        <LeadCapture 
          onComplete={handleLeadCaptureComplete}
          initialData={leadData}
        />
      )}
      
      {(currentStep === 'phase1' || currentStep === 'phase2' || currentStep === 'phase3') && (
        <QuestionPhase
          phase={currentStep}
          questions={questions[currentStep]}
          onComplete={handlePhaseComplete}
          initialAnswers={answers}
        />
      )}
      
      {currentStep === 'results' && userProfile && (
        <ResultsPage
          profile={userProfile}
          profileData={profiles[userProfile]}
          userData={leadData}
          answers={answers}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}

export default App;
